import { useCallback, useEffect, useRef, useState } from 'react';
import { Story, STORY_STATUSES } from 'types';

import { getAllStories } from '../../../../../services';
import { FiltersType } from '../types/filtersType';

const useStories = () => {
  // Loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Data
  const [data, setData] = useState<Story[]>([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState<FiltersType | undefined>({
    field: 'status',
    operator: 'in',
    value: [STORY_STATUSES.PUBLISHED, STORY_STATUSES.DRAFT],
  });
  const [expirationFilter, setExpirationFilter] = useState<FiltersType | undefined>();
  const attachedFilters = useRef({
    statusFilter,
    expirationFilter,
  });

  // Functions
  const fetch = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);
    const filters = [];
    const { statusFilter, expirationFilter } = attachedFilters.current;
    if (statusFilter) {
      filters.push(statusFilter);
    }
    if (expirationFilter) {
      filters.push(expirationFilter);
    }

    const data = await getAllStories({ filters });
    setData(data);
    setIsLoading(false);
  }, [statusFilter, expirationFilter, isLoading]);

  const handleSetStatusFilter = (value: string) => {
    setStatusFilter({
      field: 'status',
      operator: '==',
      value,
    });
  };

  const handleSetExpirationFilter = (isExpiring: boolean) => {
    setExpirationFilter({
      field: 'expirationDate',
      operator: isExpiring ? '!=' : '==',
      value: null,
    });
  };

  const resetStatusFilter = () =>
    setStatusFilter({
      field: 'status',
      operator: 'in',
      value: [STORY_STATUSES.PUBLISHED, STORY_STATUSES.DRAFT],
    });

  const resetExpirationFilter = () => setExpirationFilter(undefined);

  const resetFilters = () => {
    resetStatusFilter();
    resetExpirationFilter();
  };

  // Fetch hook
  useEffect(() => {
    attachedFilters.current.statusFilter = statusFilter;
    attachedFilters.current.expirationFilter = expirationFilter;

    fetch();
  }, [statusFilter, expirationFilter]);

  return {
    data,
    isLoading,
    statusFilter,
    expirationFilter,

    fetch,
    resetFilters,
    handleSetStatusFilter,
    handleSetExpirationFilter,
    resetStatusFilter,
    resetExpirationFilter,
  };
};

export default useStories;
